import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
// Functions
import { getProtocolTitle, diagnosisProtocols } from "../../../../config/validateProtocols";
// Components
import {
  Container,
  Header,
  HeaderText,
  DisplayCanvasButton,
  DisplayCanvasImg,
  DataDiv,
  ColumnDataDiv,
  InputDataDiv,
  Label,
  DescriptionText,
  Textarea,
  ProtocolsDiv,
  FlexDiv,
  ProtocolName,
  SelectProtocolBtn,
  CheckboxImg,
  DateInputDiv,
  DateInput,
  ActionButtonsDiv,
  SaveButton,
  CancelButton,
} from "./styles";
import UpdateMessageButton from "../updatePrescription";
// Assets
import ArrowDownImg from "../../../../assets/icons/ico_arrow_down_blue.svg";
import ArrowUpImg from "../../../../assets/icons/ico_arrow_up_blue.svg";
import CheckboxEmptyGray from "../../../../assets/icons/ico_checkbox_empty_gray.svg";
import CheckboxDisabled from "../../../../assets/icons/ico_checkbox_disabled.svg";
import CheckboxTrue from "../../../../assets/icons/ico_checkbox_true.svg";
import CheckboxFalse from "../../../../assets/icons/ico_checkbox_false.svg";

const PrescriptionCanvas = ({
  title,
  isOnlyDisplayed,
  prescribedDate,
  dateToPrescribe,
  setDateToPrescribe,
  prescribedProtocols,
  protocolsToPrescribe,
  setProtocolsToPrescribe,
  diagnosisObservation,
  diagnosisObservationToAdd,
  setDiagnosisObservationToAdd,
  setIsLoadingPrescriptions,
  setIsShowingPrescriptionForm,
  rechargePrescriptions,
  setRechargePrescriptions,
  savePrescription,
}) => {
  const [isShowingCanvas, setIsShowingCanvas] = useState(false);
  const [t] = useTranslation("global");

  const renderHeader = () => {
    return (
      <Header>
        <HeaderText width={isOnlyDisplayed ? "auto" : "225px"} showAlways={true}>
          {title}
        </HeaderText>
        {!isOnlyDisplayed && isShowingCanvas && (
          <UpdateMessageButton
            prescribedDate={prescribedDate}
            dateToPrescribe={dateToPrescribe}
            setDateToPrescribe={setDateToPrescribe}
            prescribedProtocols={prescribedProtocols}
            protocolsToPrescribe={protocolsToPrescribe}
            setProtocolsToPrescribe={setProtocolsToPrescribe}
            diagnosisObservation={diagnosisObservation}
            diagnosisObservationToAdd={diagnosisObservationToAdd}
            setDiagnosisObservationToAdd={setDiagnosisObservationToAdd}
            setIsLoadingPrescriptions={setIsLoadingPrescriptions}
            rechargePrescriptions={rechargePrescriptions}
            setRechargePrescriptions={setRechargePrescriptions}
          />
        )}
        {!isOnlyDisplayed && (
          <DisplayCanvasButton onClick={() => setIsShowingCanvas(!isShowingCanvas)}>
            <DisplayCanvasImg src={isShowingCanvas ? ArrowUpImg : ArrowDownImg} alt="ico-arrow" />
          </DisplayCanvasButton>
        )}
      </Header>
    );
  };

  const renderPrompt = () => {
    return (
      <DataDiv addTopBorder addBottomBorder>
        <ColumnDataDiv width="100%">
          <DescriptionText>{t("diagnosisProtocols_prompt")}</DescriptionText>
        </ColumnDataDiv>
      </DataDiv>
    );
  };

  const renderPrescriptionsData = () => {
    const renderDiagnosisProtocols = () => {
      const cloneArray = (array) => JSON.parse(JSON.stringify(array));
      const currentProtocolsToPrescribe = isOnlyDisplayed ? cloneArray(protocolsToPrescribe) : cloneArray(prescribedProtocols);

      return diagnosisProtocols.map((protocol) => {
        const isMarkedProtocol = currentProtocolsToPrescribe.some(
          (protocolToPrescribe) => protocolToPrescribe.PROTOCOL_ID === protocol.id && protocolToPrescribe.hasOwnProperty("STARTING_DATE")
        );

        const setProtocolToPrescribe = () => {
          const filteredPrescribedProtocols = currentProtocolsToPrescribe.filter(
            (protocolToPrescribe) => protocolToPrescribe.PROTOCOL_ID !== protocol.id
          );

          let currentProtocol = { PROTOCOL_ID: protocol.id };

          // Prescribing a protocol for the first time 'OR' Prescribing a protocol again
          if (!isMarkedProtocol) currentProtocol.STARTING_DATE = dateToPrescribe;

          setProtocolsToPrescribe([...filteredPrescribedProtocols, currentProtocol]);
        };

        return (
          <FlexDiv key={protocol.id}>
            <SelectProtocolBtn onClick={setProtocolToPrescribe} disabled={!isOnlyDisplayed}>
              <CheckboxImg
                src={
                  !isOnlyDisplayed
                    ? isMarkedProtocol
                      ? CheckboxDisabled
                      : CheckboxEmptyGray
                    : isMarkedProtocol
                    ? CheckboxTrue
                    : CheckboxFalse
                }
                alt="check-ico"
              />
            </SelectProtocolBtn>
            <ProtocolName onClick={setProtocolToPrescribe} disabled={!isOnlyDisplayed}>
              {t(getProtocolTitle("protocols", protocol.id))}
            </ProtocolName>
          </FlexDiv>
        );
      });
    };

    const handleSetDate = (date) => {
      const parsedDate = moment(date ? date : new Date()).format("YYYY-MM-DD");
      setDateToPrescribe(parsedDate);

      const currentProtocolsToPrescribe = protocolsToPrescribe.map((protocolToPrescribe) => {
        if (protocolToPrescribe.hasOwnProperty("STARTING_DATE")) protocolToPrescribe.STARTING_DATE = parsedDate;
        return protocolToPrescribe;
      });

      setProtocolsToPrescribe(currentProtocolsToPrescribe);
    };

    return (
      <DataDiv addBottomBorder={isOnlyDisplayed}>
        <ColumnDataDiv width="385px" flexDirection="column">
          {/* Starting date */}
          <InputDataDiv>
            <Label fontWeight="bold">{t("diagnosisProtocols_data_prescriptionDate")} *</Label>
            <DateInputDiv>
              <DateInput
                placeholderText={"dd/mm/yyyy"}
                selected={isOnlyDisplayed ? new Date(dateToPrescribe) : new Date(prescribedDate)}
                onChange={(date) => handleSetDate(date)}
                dateFormat={"dd/MM/yyyy"}
                disabled={!isOnlyDisplayed}
              />
            </DateInputDiv>
          </InputDataDiv>
          {/* Observation */}
          <InputDataDiv alignItems="flex-start">
            <Label>{t("diagnosisProtocols_data_observation")}</Label>
            <Textarea
              placeholder={isOnlyDisplayed ? `${t("diagnosisProtocols_data_observationPlaceholder")}...` : diagnosisObservation}
              value={isOnlyDisplayed ? diagnosisObservationToAdd : diagnosisObservation}
              onChange={(e) => setDiagnosisObservationToAdd(e.target.value)}
              disabled={!isOnlyDisplayed}
            />
          </InputDataDiv>
        </ColumnDataDiv>
        <ColumnDataDiv width="385px" flexDirection910="column">
          <Label fontWeight="bold">{t("activities")} *</Label>
          <ProtocolsDiv>{renderDiagnosisProtocols()}</ProtocolsDiv>
        </ColumnDataDiv>
      </DataDiv>
    );
  };

  const renderActionButtons = () => {
    if (isOnlyDisplayed) {
      return (
        <ActionButtonsDiv>
          <SaveButton onClick={savePrescription}>{t("save")}</SaveButton>
          <CancelButton onClick={() => setIsShowingPrescriptionForm(false)}>{t("cancel")}</CancelButton>
        </ActionButtonsDiv>
      );
    }
  };

  return (
    <Container>
      {renderHeader()}
      {(isOnlyDisplayed || isShowingCanvas) && (
        <>
          {renderPrompt()}
          {renderPrescriptionsData()}
          {renderActionButtons()}
        </>
      )}
    </Container>
  );
};

export default PrescriptionCanvas;
